import { AimOutlined, BankOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type {
  OrgInstRole,
  OrgInstType,
  OrgRow,
  OrgSaveRequest,
  OrgStatus,
  OrgSuperviseLevel
} from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { checkShortName, formatDisplayTime, SHORT_NAME_HINT } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { LogoField, RegionAddressFields, COL, sumWidth } from '@hq/ui-kit';
import type { HqApi } from '@hq/api-sdk';

const INST_TYPE_META: Record<OrgInstType, string> = {
  HOSPITAL: '医院',
  CHC: '社区医院',
  EMERGENCY: '急救中心',
  CDC: '疾控中心'
};

const INST_ROLE_META: Record<OrgInstRole, { text: string; color: string }> = {
  MAIN_HOSPITAL: { text: '主院', color: 'blue' },
  BRANCH: { text: '分院', color: 'cyan' },
  COMMUNITY_MEMBER: { text: '医共体成员', color: 'geekblue' },
  INDEPENDENT: { text: '独立机构', color: 'default' }
};

const STATUS_META: Record<OrgStatus, { text: string; color: string }> = {
  INIT: { text: '筹建', color: 'default' },
  RUNNING: { text: '运行', color: 'green' },
  SUSPEND: { text: '暂停', color: 'orange' },
  EXPIRED: { text: '到期', color: 'red' }
};

interface OrgFormValues {
  orgCode: string;
  orgName: string;
  /** 简称：2–6 个汉字。 */
  orgShortName: string;
  /** LOGO 地址。 */
  logoUrl?: string;
  instType: OrgInstType;
  instRole: OrgInstRole;
  parentOrgId?: string;
  /** 所属医共体 / 集团。 */
  groupId?: string;
  /** 可监管深度：仅本院 / 仅直属 / 全部下级。 */
  superviseLevel?: OrgSuperviseLevel;
  /** 级联显示值（省/市/区代码数组），落库仍走下面三个字段。 */
  regionCodes?: string[];
  provinceCode?: string;
  cityCode?: string;
  districtCode?: string;
  address?: string;
  lng?: number | null;
  lat?: number | null;
  contactPerson?: string;
  contactPhone?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  slaEmergencyHours: number;
  slaNormalDays: number;
  status: OrgStatus;
  remark?: string;
  /** 仅新建：机构所属租户（平台代管跨租户建档）。 */
  tenantId?: string;
}

/**
 * 机构管理（`org_institution`）：医院 / 分院 / 医共体成员 / 社区医院统一在运营端录入。
 * 简称强制 2–6 个汉字；层级通过「上级机构」维护，后端同步 `path_ids` 供数据权限向下穿透。
 */
export interface OrgManagePageProps {
  hq: HqApi;
  /** 平台视角（web-system）：多租户筛选 + 「所属租户」列 + 新建时选租户；运营端固定本租户。 */
  platform?: boolean;
}

export function OrgManagePage({ hq, platform = false }: OrgManagePageProps) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [keyword, setKeyword] = useState('');
  const [instType, setInstType] = useState('');
  const [status, setStatus] = useState('');
  // 平台视角：按租户筛选，未选表示全部租户（platformAll 决定可见范围）
  const [tenantId, setTenantId] = useState<string | undefined>();
  const [open, setOpen] = useState(false);

  const tenants = useQuery({
    queryKey: ['sys', 'tenant', 'options'],
    queryFn: () => hq.tenant.list({}),
    enabled: platform,
    staleTime: 60 * 1000
  });
  const [editing, setEditing] = useState<OrgRow | null>(null);
  const [form] = Form.useForm<OrgFormValues>();

  const groups = useQuery({
    queryKey: ['ops', 'org-group', 'list'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.orgGroup.list(),
    staleTime: 60 * 1000
  });

  // 方案 A：医共体成员 = 归口到某个医共体，且上级机构必须是该组的牵头机构
  const instRoleWatch = Form.useWatch('instRole', form) as OrgInstRole | undefined;
  const isCommunityMember = instRoleWatch === 'COMMUNITY_MEMBER';

  const orgs = useQuery({
    queryKey: ['ops', 'org', 'list', keyword, instType, status, platform ? tenantId : undefined],
    placeholderData: keepPreviousData,
    queryFn: () =>
      hq.org.list({
        keyword: keyword || undefined,
        instType: (instType || undefined) as OrgInstType | undefined,
        status: (status || undefined) as OrgStatus | undefined,
        tenantId: platform ? tenantId : undefined
      })
  });

  const save = useMutation({
    mutationFn: (body: OrgSaveRequest) => hq.org.save(body),
    onSuccess: async (result) => {
      message.success(result.updated ? '机构已更新' : '机构已新增');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'org'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const remove = useMutation({
    mutationFn: (id: string) => hq.org.remove(id),
    onSuccess: async () => {
      message.success('机构已删除');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'org'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败，请稍后重试');
    }
  });

  const rows = orgs.data ?? [];
  const error = orgs.error instanceof ApiError ? orgs.error.friendlyMessage : null;

  /** 存量机构补坐标：只处理「有地址缺经纬度」的记录（后端每表每次 100 条）。 */
  const [backfilling, setBackfilling] = useState(false);
  const backfill = async () => {
    setBackfilling(true);
    try {
      const result = await hq.geo.backfill();
      const updated = result.reduce((sum, row) => sum + row.updated, 0);
      const failed = result.reduce((sum, row) => sum + row.failed, 0);
      message.success(
        `坐标回填完成：更新 ${updated} 条${failed ? `，未匹配 ${failed} 条（地址需补充到门牌号）` : ''}`
      );
      await queryClient.invalidateQueries({ queryKey: ['ops', 'org'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : '坐标回填失败');
    } finally {
      setBackfilling(false);
    }
  };

  const parentOptions = rows
    .filter((row) => row.id !== editing?.id)
    .map((row) => ({
      value: row.id,
      label: `${'　'.repeat(Math.max(0, row.levelNo - 1))}${row.orgName}（${row.orgShortName ?? '-'}）`
    }));

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({
      instType: 'HOSPITAL',
      instRole: 'MAIN_HOSPITAL',
      // 已在列表按租户筛选时，默认沿用该租户，减少漏选
      tenantId,
      slaEmergencyHours: 2,
      slaNormalDays: 1,
      status: 'RUNNING'
    });
    setOpen(true);
  };

  const openEdit = (row: OrgRow) => {
    setEditing(row);
    form.setFieldsValue({
      orgCode: row.orgCode,
      orgName: row.orgName,
      orgShortName: row.orgShortName ?? '',
      instType: row.instType,
      instRole: row.instRole,
      parentOrgId: row.parentOrgId === '0' ? undefined : row.parentOrgId,
      groupId: row.groupId === '0' ? undefined : row.groupId,
      logoUrl: row.logoUrl ?? undefined,
      superviseLevel: row.superviseLevel ?? undefined,
      // 级联显示值由 RegionAddressFields 按 districtCode 自动补全
      regionCodes: undefined,
      provinceCode: row.provinceCode ?? undefined,
      cityCode: row.cityCode ?? undefined,
      districtCode: row.districtCode ?? row.regionCode ?? undefined,
      address: row.address ?? undefined,
      lng: row.lng,
      lat: row.lat,
      contactPerson: row.contactPerson ?? undefined,
      contactPhone: row.contactPhone ?? undefined,
      emergencyContactName: row.emergencyContactName ?? undefined,
      emergencyContactPhone: row.emergencyContactPhone ?? undefined,
      slaEmergencyHours: row.slaEmergencyHours,
      slaNormalDays: row.slaNormalDays,
      status: row.status,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const submit = (values: OrgFormValues) => {
    save.mutate({
      id: editing?.id,
      orgCode: values.orgCode,
      orgName: values.orgName,
      orgShortName: values.orgShortName,
      instType: values.instType,
      instRole: values.instRole,
      parentOrgId: values.parentOrgId ?? '0',
      groupId: values.groupId ?? '0',
      logoUrl: values.logoUrl,
      superviseLevel: values.superviseLevel,
      provinceCode: values.provinceCode,
      cityCode: values.cityCode,
      districtCode: values.districtCode,
      regionCode: values.districtCode,
      address: values.address,
      lng: values.lng ?? null,
      lat: values.lat ?? null,
      contactPerson: values.contactPerson,
      contactPhone: values.contactPhone,
      emergencyContactName: values.emergencyContactName,
      emergencyContactPhone: values.emergencyContactPhone,
      slaEmergencyHours: values.slaEmergencyHours,
      slaNormalDays: values.slaNormalDays,
      status: values.status,
      remark: values.remark,
      // 更新时不改租户，避免把机构搬到别的租户；运营端不能自选租户（凭令牌落本租户）
      tenantId: platform && !editing ? values.tenantId : undefined
    });
  };

  /** 截断悬浮提示统一用 antd Tooltip（挂 body），避免 native title 浮层落在滚动容器外/错位 */
  const ellipsisTip = (content: ReactNode, title: ReactNode) => (
    <Tooltip title={title} placement="topLeft">
      <Typography.Text ellipsis style={{ display: 'block', maxWidth: '100%' }}>
        {content}
      </Typography.Text>
    </Tooltip>
  );

  const columns: ColumnsType<OrgRow> = [
    // 平台跨租户维护：先标明机构属于哪个租户，避免误操作到别的租户
    ...(platform
      ? [
          {
            title: '所属租户',
            dataIndex: 'tenantName',
            width: COL.dept,
            ellipsis: { showTitle: false },
            render: (value: string | null, row: OrgRow) => {
              const text = value ?? (row.tenantId === '0' ? '平台' : `#${row.tenantId}`);
              return ellipsisTip(text, text);
            }
          } as ColumnsType<OrgRow>[number]
        ]
      : []),
    {
      // 主描述列：单行「LOGO + 全称 + 简称」，编码/层级/时间收进悬浮提示，避免行高被拉高
      title: '机构全称',
      dataIndex: 'orgName',
      ellipsis: { showTitle: false },
      render: (value: string, row) => (
        <Tooltip
          placement="topLeft"
          title={`${row.orgCode} · 层级 ${row.levelNo} · ${formatDisplayTime(row.updateAt ?? row.createAt)}`}
        >
          <Space size={6} align="center">
            {row.logoUrl ? (
              <img
                src={row.logoUrl}
                alt=""
                style={{ height: 20, width: 20, objectFit: 'contain', borderRadius: 3, border: '1px solid #f0f0f0', flex: '0 0 auto' }}
              />
            ) : null}
            <Typography.Text ellipsis style={{ maxWidth: 240 }}>
              {value}
            </Typography.Text>
            {row.orgShortName ? <Tag style={{ marginInlineEnd: 0, flexShrink: 0 }}>{row.orgShortName}</Tag> : null}
          </Space>
        </Tooltip>
      )
    },
    {
      title: '类型',
      dataIndex: 'instType',
      width: COL.status,
      render: (value: OrgInstType) => INST_TYPE_META[value] ?? value
    },
    {
      title: '角色',
      dataIndex: 'instRole',
      width: COL.status,
      render: (value: OrgInstRole) => {
        const meta = INST_ROLE_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '上级机构',
      dataIndex: 'parentOrgName',
      width: COL.dept,
      ellipsis: { showTitle: false },
      render: (value: string | null) => (value ? ellipsisTip(value, value) : '—')
    },
    {
      title: '所属医共体',
      dataIndex: 'groupName',
      width: COL.dept,
      ellipsis: { showTitle: false },
      render: (value: string | null) => (value ? ellipsisTip(value, value) : '—')
    },
    {
      // 地址/GPS 拆成两列单行展示：区划+坐标收进悬浮提示
      title: '地址',
      dataIndex: 'address',
      width: COL.bizNo,
      ellipsis: { showTitle: false },
      render: (value: string | null, row) => {
        const district = row.districtCode ?? row.regionCode ?? '未选区划';
        const coords =
          row.lng != null && row.lat != null
            ? `${district} · ${Number(row.lng).toFixed(5)}, ${Number(row.lat).toFixed(5)}`
            : `${district} · 无坐标`;
        return value ? ellipsisTip(value, coords) : '—';
      }
    },
    {
      title: 'GPS',
      key: 'gps',
      width: COL.time,
      render: (_, row) =>
        row.lng != null && row.lat != null ? (
          <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {Number(row.lng).toFixed(5)}, {Number(row.lat).toFixed(5)}
          </span>
        ) : (
          <Typography.Text type="secondary">无坐标</Typography.Text>
        )
    },
    {
      // 联络人/电话单行合并，电话收进悬浮提示
      title: '联络人',
      key: 'contact',
      width: COL.dept,
      render: (_, row) => {
        if (!row.contactPerson && !row.contactPhone) return '-';
        const text = [row.contactPerson ?? '-', row.contactPhone].filter(Boolean).join(' ');
        return (
          <Tooltip title={`${row.contactPerson ?? '-'} ${row.contactPhone ?? ''}`} placement="topLeft">
            <Typography.Text ellipsis>{text}</Typography.Text>
          </Tooltip>
        );
      }
    },
    {
      title: 'SLA(应急/常规)',
      width: COL.dept,
      render: (_, row) => `${row.slaEmergencyHours}小时/${row.slaNormalDays}天`
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: COL.flag,
      render: (value: OrgStatus) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '操作',
      width: COL.action2,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Popconfirm
            title="删除该机构？"
            description={
              row.childCount > 0
                ? `该机构下还有 ${row.childCount} 个下级机构，需先处理下级`
                : '存在仓库/科室/账号时后端会拒绝删除'
            }
            okText="删除"
            cancelText="取消"
            onConfirm={() => remove.mutate(row.id)}
          >
            <Button type="link" size="small" danger loading={remove.isPending}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          <BankOutlined />
          机构管理
        </Space>
      }
      extra={
        <Space>
          <Button icon={<AimOutlined />} loading={backfilling} onClick={() => void backfill()}>
            批量解析坐标
          </Button>
          <Button icon={<PlusOutlined />} type="primary" onClick={openCreate}>
            新建机构
          </Button>
          <Button icon={<ReloadOutlined />} loading={orgs.isFetching} onClick={() => void orgs.refetch()}>
            刷新
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={`机构简称：${SHORT_NAME_HINT}；LOGO、省市区、详细地址为必填`}
        description="医院、分院、医共体成员、社区医院统一在此录入。省/市/区来自「行政区划」字典（已导入全国数据），地址经高德解析为经纬度保存；分院/医共体成员必须挂上级机构，层级会自动写入 path_ids，供数据权限（SUPERVISED_ORG）向下穿透。"
      />

      <Space wrap style={{ marginBottom: 12 }}>
        {platform ? (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="全部租户"
            style={{ width: 220 }}
            value={tenantId}
            onChange={(value) => setTenantId(value ?? undefined)}
            options={(tenants.data ?? []).map((item) => ({
              value: String(item.id),
              label: `${item.tenantName}（${item.tenantCode}）`
            }))}
          />
        ) : null}
        <Input.Search
          allowClear
          placeholder="机构名称 / 编码 / 简称"
          style={{ width: 240 }}
          onSearch={setKeyword}
        />
        <Select
          value={instType}
          style={{ width: 140 }}
          onChange={setInstType}
          options={[
            { value: '', label: '全部类型' },
            { value: 'HOSPITAL', label: '医院' },
            { value: 'CHC', label: '社区医院' },
            { value: 'EMERGENCY', label: '急救中心' },
            { value: 'CDC', label: '疾控中心' }
          ]}
        />
        <Select
          value={status}
          style={{ width: 130 }}
          onChange={setStatus}
          options={[
            { value: '', label: '全部状态' },
            { value: 'INIT', label: '筹建' },
            { value: 'RUNNING', label: '运行' },
            { value: 'SUSPEND', label: '暂停' },
            { value: 'EXPIRED', label: '到期' }
          ]}
        />
      </Space>

      {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message="加载失败" description={error} /> : null}

      <Table<OrgRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={orgs.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无机构，点击「新建机构」录入' }}
      />

      <Modal
        title={editing ? '编辑机构' : '新建机构'}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        width={720}
        destroyOnHidden
      >
        <Form<OrgFormValues> form={form} layout="vertical" onFinish={submit}>
          {/* 平台代管跨租户建机构：必须显式选租户，否则顶层机构会落到 tenant_id=0 的平台桶 */}
          {platform && !editing ? (
            <Form.Item
              name="tenantId"
              label="所属租户"
              rules={[{ required: true, message: '请选择所属租户' }]}
              extra="平台账号新建顶层机构时必须指定租户；已有上级机构时会跟随上级租户。"
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="选择机构所属租户"
                loading={tenants.isLoading}
                options={(tenants.data ?? []).map((row) => ({
                  value: row.id,
                  label: row.tenantName
                }))}
              />
            </Form.Item>
          ) : null}
          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="orgCode"
              label="机构编码"
              rules={[
                { required: true, message: '请填写机构编码' },
                { pattern: /^[0-9A-Za-z_-]{2,32}$/, message: '字母、数字、下划线或短横线，2–32 位' }
              ]}
              style={{ width: 220 }}
            >
              <Input placeholder="如 330102001" maxLength={32} />
            </Form.Item>
            <Form.Item
              name="orgName"
              label="机构全称"
              rules={[{ required: true, message: '请填写机构全称' }]}
              style={{ width: 440 }}
            >
              <Input placeholder="如 杭州市行政第一人民医院" maxLength={128} />
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item
              name="orgShortName"
              label="机构简称（2–6 个汉字）"
              rules={[
                { required: true, message: '请填写机构简称' },
                {
                  validator: (_, value: string) => {
                    const error = checkShortName(value);
                    return error ? Promise.reject(new Error(error)) : Promise.resolve();
                  }
                }
              ]}
              style={{ width: 260 }}
            >
              <Input placeholder="如 区一院" maxLength={6} showCount />
            </Form.Item>
            <Form.Item
              name="instType"
              label="机构类型"
              rules={[{ required: true, message: '请选择机构类型' }]}
              style={{ width: 180 }}
            >
              <Select
                options={[
                  { value: 'HOSPITAL', label: '医院' },
                  { value: 'CHC', label: '社区医院' },
                  { value: 'EMERGENCY', label: '急救中心' },
                  { value: 'CDC', label: '疾控中心' }
                ]}
              />
            </Form.Item>
            <Form.Item
              name="instRole"
              label="机构角色"
              rules={[{ required: true, message: '请选择机构角色' }]}
              style={{ width: 180 }}
            >
              <Select
                options={[
                  { value: 'MAIN_HOSPITAL', label: '主院' },
                  { value: 'BRANCH', label: '分院' },
                  { value: 'COMMUNITY_MEMBER', label: '医共体成员' },
                  { value: 'INDEPENDENT', label: '独立机构' }
                ]}
                onChange={(value: OrgInstRole) => {
                  // 方案 A：成员单位的上级机构 = 所属医共体的牵头机构，选角色时即带出
                  if (value !== 'COMMUNITY_MEMBER') return;
                  const group = (groups.data ?? []).find((item) => item.id === form.getFieldValue('groupId'));
                  if (group?.leadOrgId && group.leadOrgId !== '0') {
                    form.setFieldValue('parentOrgId', group.leadOrgId);
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              name="status"
              label="状态"
              rules={[{ required: true, message: '请选择状态' }]}
              style={{ width: 140 }}
            >
              <Select
                options={[
                  { value: 'INIT', label: '筹建' },
                  { value: 'RUNNING', label: '运行' },
                  { value: 'SUSPEND', label: '暂停' },
                  { value: 'EXPIRED', label: '到期' }
                ]}
              />
            </Form.Item>
          </Space>

          <Form.Item
            name="parentOrgId"
            label="上级机构"
            dependencies={['instRole', 'groupId']}
            extra={
              isCommunityMember
                ? '医共体成员单位的上级机构固定为该医共体的牵头机构，不可另选'
                : '主院 / 独立机构留空即一级机构；分院挂在所属医院下'
            }
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const role = getFieldValue('instRole') as OrgInstRole | undefined;
                  const needParent = role === 'BRANCH' || role === 'COMMUNITY_MEMBER';
                  if (!needParent || value) return Promise.resolve();
                  return Promise.reject(
                    new Error(
                      role === 'COMMUNITY_MEMBER'
                        ? '请先选择所属医共体（成员的上级机构取该医共体牵头机构）'
                        : '分院必须选择上级机构（所属医院）'
                    )
                  );
                }
              })
            ]}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="主院/独立机构可留空"
              disabled={isCommunityMember}
              options={parentOptions}
            />
          </Form.Item>

          <Form.Item
            name="groupId"
            label="所属医共体 / 集团"
            dependencies={['instRole']}
            extra="医共体只做归口与合同口径，不占机构树层级；选择后成员单位会自动挂到其牵头机构下"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const role = getFieldValue('instRole') as OrgInstRole | undefined;
                  if (role !== 'COMMUNITY_MEMBER' || value) return Promise.resolve();
                  return Promise.reject(new Error('医共体成员必须选择所属医共体'));
                }
              })
            ]}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={isCommunityMember ? '必选（成员单位的归口）' : '不挂靠'}
              options={(groups.data ?? []).map((item) => ({
                value: item.id,
                label: item.leadOrgName ? `${item.groupName}（牵头：${item.leadOrgName}）` : item.groupName
              }))}
              onChange={(value?: string) => {
                if (!isCommunityMember) return;
                const group = (groups.data ?? []).find((item) => item.id === value);
                form.setFieldValue('parentOrgId', group?.leadOrgId && group.leadOrgId !== '0' ? group.leadOrgId : undefined);
              }}
            />
          </Form.Item>

          <Form.Item
            name="superviseLevel"
            label="可监管深度"
            extra="决定该机构账号（切换机构/监管看板）能看到哪些下级：仅本院 / 仅直属下级 / 递归全部下级"
            style={{ width: 320 }}
          >
            <Select
              allowClear
              placeholder="新建时按角色默认（主院/卫健局=全部下级）"
              options={[
                { value: 'SELF_ONLY', label: '仅本院' },
                { value: 'DIRECT', label: '仅直属下级' },
                { value: 'ALL_SUB', label: '递归全部下级' }
              ]}
            />
          </Form.Item>

          <LogoField hq={hq} form={form} label="机构 LOGO" bizType="ORG_LOGO" />

          <RegionAddressFields hq={hq} form={form} addressHint="机构地址用于高德解析坐标，配送范围与监管地图取数依赖它" />

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="contactPerson" label="联络人" style={{ width: 160 }}>
              <Input maxLength={32} />
            </Form.Item>
            <Form.Item
              name="contactPhone"
              label="联系电话"
              style={{ width: 180 }}
              rules={[
                {
                  pattern: /^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8}(-\d{1,6})?)$/,
                  message: '手机号或含区号座机'
                }
              ]}
            >
              <Input maxLength={20} />
            </Form.Item>
            <Form.Item name="slaEmergencyHours" label="应急到货 SLA(小时)" style={{ width: 170 }}>
              <InputNumber min={0.5} max={72} step={0.5} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="slaNormalDays" label="常规到货(天)" style={{ width: 150 }}>
              <InputNumber min={0} max={30} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Space size={16} wrap style={{ width: '100%' }} align="start">
            <Form.Item name="emergencyContactName" label="应急联系人" style={{ width: 200 }}>
              <Input maxLength={32} />
            </Form.Item>
            <Form.Item
              name="emergencyContactPhone"
              label="应急电话"
              style={{ width: 220 }}
              rules={[
                {
                  pattern: /^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8}(-\d{1,6})?)$/,
                  message: '手机号或含区号座机'
                }
              ]}
            >
              <Input maxLength={20} />
            </Form.Item>
          </Space>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
